import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { SearchOverlay } from "./SearchOverlay";
import { useCart } from "../context/CartContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/candles", label: "Candles" },
  { to: "/jewellery", label: "Jewellery" },
  { to: "/fashion", label: "Fashion" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const { count } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-beige bg-ivory/95 shadow-[0_10px_40px_rgba(42,33,26,0.07)] backdrop-blur-md"
            : "border-b border-transparent bg-ivory"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:h-24 md:px-10">
          <Logo />
          <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group relative py-2 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-ink hover:text-gold"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px w-full origin-left bg-gold transition-transform duration-500 ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-5 md:gap-6">
            <button
              data-testid="header-search-button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="text-ink transition-colors hover:text-gold"
            >
              <Search size={19} strokeWidth={1.5} />
            </button>
            <Link
              to="/account"
              data-testid="header-account-button"
              aria-label="Account"
              className="hidden text-ink transition-colors hover:text-gold sm:block"
            >
              <User size={19} strokeWidth={1.5} />
            </Link>
            <Link
              to="/cart"
              data-testid="header-cart-button"
              aria-label="Shopping bag"
              className="relative text-ink transition-colors hover:text-gold"
            >
              <ShoppingBag size={19} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[9px] font-medium text-ivory"
                >
                  {count}
                </span>
              )}
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="text-ink lg:hidden"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] flex flex-col bg-ivory"
            data-testid="mobile-menu"
          >
            <div className="flex h-20 items-center justify-between px-5">
              <Logo />
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-center justify-center gap-7">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) => `font-serif text-3xl ${isActive ? "text-gold" : "text-ink"}`}
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55 }}
                className="mt-8 text-[10px] uppercase tracking-[0.3em] text-taupe"
              >
                Timeless Elegance. In Every Detail.
              </motion.p>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};
